import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhanceTableHeaderTypes,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { Helmet } from 'react-helmet-async';
import Loader from 'src/components/Loader';
import { DataService } from 'src/services/data-service';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'src/redux/hooks';
import { useNavigate } from 'react-router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Divider
} from '@mui/material';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import AddJob from './add-job';
import EditJob from './edit-job';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import WysiwygIcon from '@mui/icons-material/Wysiwyg';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

interface IJobListing {
  id: string;
  type: string;
  title: string;
  location: string;
  description: string;
  active: string;
  createdOn: Date;
  createdBy: string;
  updatedOn: Date;
  updatedBy: string;
}

interface IJobListingResponse {
  id: string;
  type: string;
  title: string;
  location: string;
  description: string;
  active: boolean;
  createdOn: Date;
  createdBy: string;
  updatedOn: Date;
  updatedBy: string;
}

const JobListing: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<Record<string, IJobListing>>({});
  const [openAddJob, setOpenAddJob] = useState<boolean>(false);
  const [openEditJob, setOpenEditJob] = useState<boolean>(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  // State for preview dialog
  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [previewJob, setPreviewJob] = useState<IJobListing | null>(null);

  // Define table headers with row actions
  const headers: EnhanceTableHeaderTypes[] = [
    { id: 'title', label: 'Title', type: EnhancedTableColumnType.TEXT },
    { id: 'type', label: 'Type', type: EnhancedTableColumnType.TEXT },
    { id: 'location', label: 'Location', type: EnhancedTableColumnType.TEXT },
    {
      id: 'active',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.Action,
      actions: [
        {
          // Row action for previewing the job details
          onClick: (id: string) => PreviewJob(id),
          icon: <WysiwygIcon fontSize="small" />,
          label: 'Preview',
          hidden: () => false
        },
        {
          // Row action for previewing the job details
          onClick: (id: string) => handleEditJob(id),
          icon: <EditIcon fontSize="small" />,
          label: 'Edit',
          hidden: () => false
        },
        {
          // Row action for toggling status (activate/deactivate)
          onClick: (id: string) => changeStatus(id),
          icon: <ChangeCircleIcon fontSize="small" />,
          label: 'Toggle Status',
          hidden: () => false
        }
      ]
    }
  ];

  // Load all jobs
  const initialize = async () => {
    setLoading(true);
    const response = await DataService.get('api/job/all');
    if (response.ok) {
      const list: IJobListingResponse[] = await response.json();
      const result: Record<string, IJobListing> = {};
      list.forEach((c) => {
        result[c.id] = {
          id: c.id,
          type: c.type,
          title: c.title,
          location: c.location,
          description: c.description,
          active: c.active ? 'Active' : 'Inactive',
          createdOn: c.createdOn,
          createdBy: c.createdBy,
          updatedOn: c.updatedOn,
          updatedBy: c.updatedBy
        };
      });
      setData(result);
    } else {
      toast.error('Failed to fetch jobs.');
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  if (loading) {
    return <Loader size={75} />;
  }

  // Function to preview a job by opening a dialog with its details
  const PreviewJob = (id: string) => {
    const job = data[id];
    if (!job) return;
    setPreviewJob(job);
    setOpenPreview(true);
  };

  // Function to handle editing a job
  const handleEditJob = (id: string) => {
    setSelectedJobId(id);
    setOpenEditJob(true);
  };

  // Function to toggle job status based on current state
  const changeStatus = async (id: string) => {
    const job = data[id];
    if (!job) return;
    if (job.active === 'Active') {
      // If active, call the deactivate endpoint
      const response = await DataService.patch(
        `api/job/deactivate?id=${id}`,
        {}
      );
      if (response.ok) {
        toast.success('Job deactivated successfully');
        initialize();
      } else {
        toast.error('Failed to deactivate job');
      }
    } else {
      // If inactive, call the activate endpoint
      const response = await DataService.patch(`api/job/activate?id=${id}`, {});
      if (response.ok) {
        toast.success('Job activated successfully');
        initialize();
      } else {
        toast.error('Failed to activate job');
      }
    }
  };

  // Define menu actions (only for add and edit via drawer)
  const actions: ITableMenuAction[] = [
    {
      key: 'addJob',
      title: 'Add Job',
      disabled: (selected: string[]) => selected.length > 0,
      onClick: () => {
        setOpenAddJob(true);
      }
    },
    {
      key: 'editJob',
      title: 'Edit Job',
      disabled: (selected: string[]) => selected.length !== 1,
      onClick: (selected: string[]) => {
        if (selected.length === 1) {
          setSelectedJobId(selected[0]);
          setOpenEditJob(true);
        }
      }
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Job Listing</title>
      </Helmet>
      <MainPageTitle
        title="Job List"
        action={{
          title: 'Add Job',
          onClick: () => {
            setOpenAddJob(true);
          }
        }}
      />
      <EnhancedTable
        title="Jobs"
        header={headers}
        data={data}
        actions={actions}
      />

      {/* Add Job Drawer */}
      {openAddJob && (
        <AddJob
          onSuccess={() => {
            setOpenAddJob(false);
            initialize();
          }}
          onClose={() => {
            setOpenAddJob(false);
          }}
        />
      )}

      {/* Edit Job Drawer */}
      {openEditJob && selectedJobId && (
        <EditJob
          id={selectedJobId}
          onSuccess={() => {
            setOpenEditJob(false);
            setSelectedJobId(null);
            initialize();
          }}
          onClose={() => {
            setOpenEditJob(false);
            setSelectedJobId(null);
          }}
        />
      )}

      {/* Preview Job Dialog */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon color="primary" /> Job Details
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {previewJob ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Title */}
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon color="secondary" />
                <Typography variant="h6" fontWeight="bold">
                  {previewJob.title}
                </Typography>
              </Box>

              {/* Type */}
              <Box display="flex" alignItems="center" gap={1}>
                <WorkIcon color="primary" />
                <Typography variant="body1">
                  <strong>Type:</strong> {previewJob.type}
                </Typography>
              </Box>

              {/* Location */}
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOnIcon color="action" />
                <Typography variant="body1">
                  <strong>Location:</strong> {previewJob.location}
                </Typography>
              </Box>

              {/* Description */}
              <Box display="flex" alignItems="center" gap={1}>
                <DescriptionIcon color="info" />
                <Typography variant="body1">
                  <strong>Description:</strong> {previewJob.description}
                </Typography>
              </Box>

              <Divider />

              {/* Status */}
              <Box display="flex" alignItems="center" gap={1}>
                {previewJob.active === 'Active' ? (
                  <>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body1" color="green">
                      <strong>Status:</strong> Active
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelIcon color="error" />
                    <Typography variant="body1" color="red">
                      <strong>Status:</strong> Inactive
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenPreview(false)}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default JobListing;
